import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

export default function VoiceConfigPage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState('');
  const [testMsg, setTestMsg] = useState('Cześć, to jest test połączenia z Torr.pl.');
  const [calling, setCalling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/voice-config');
      setCfg(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateProvider = async (prov, patch) => {
    try {
      await api.put('/voice-config', { providers: { [prov]: patch } });
      toast.success(`${prov} zapisany`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
  };

  const setDefault = async (prov) => {
    try {
      await api.put('/voice-config', { default_provider: prov });
      toast.success(`Default: ${prov}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
  };

  const testCall = async () => {
    if (!testTo.trim()) return toast.error('Podaj numer');
    setCalling(true);
    try {
      const r = await api.post('/voice-config/call', { to_number: testTo.trim(), message: testMsg });
      toast.success(`Zadzwoniono. Call ID: ${r.data.call_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Błąd');
    } finally { setCalling(false); }
  };

  if (loading || !cfg) return <div className="p-6"><Skeleton className="h-32" /></div>;

  const providers = cfg.providers || {};

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-heading font-semibold">Voice providery (Twilio/Vonage)</h1>
        <p className="text-sm text-muted-foreground">Konfiguracja + test wywołań. Bez klucza — endpointy zwracają 503, nic nie crashuje.</p>
      </div>

      {/* TWILIO */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold flex items-center gap-2">
              Twilio {providers.twilio?.enabled && providers.twilio?.auth_token_set ? <Badge>aktywny</Badge> : <Badge variant="outline" className="opacity-50">nieaktywny</Badge>}
            </h3>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!providers.twilio?.enabled} onChange={(e) => updateProvider('twilio', { enabled: e.target.checked })} /> enabled
            </label>
          </div>
          <div>
            <Label>Account SID</Label>
            <Input value={providers.twilio?.account_sid || ''} onChange={(e) => updateProvider('twilio', { account_sid: e.target.value })} placeholder="ACxxxxx..." />
          </div>
          <div>
            <Label>Auth token {providers.twilio?.auth_token_set && <span className="text-xs opacity-60">({providers.twilio.auth_token})</span>}</Label>
            <Input type="password" placeholder="wpisz auth_token (Enter zapisze)" onKeyDown={(e) => { if (e.key === 'Enter') { updateProvider('twilio', { auth_token: e.target.value }); e.target.value = ''; } }} />
          </div>
          <div>
            <Label>From number (Twój Twilio number, format +48...)</Label>
            <Input value={providers.twilio?.from_number || ''} onChange={(e) => updateProvider('twilio', { from_number: e.target.value })} placeholder="+48..." />
          </div>
          <Button variant="outline" size="sm" onClick={() => setDefault('twilio')}>Ustaw jako default</Button>
        </CardContent>
      </Card>

      {/* VONAGE */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold flex items-center gap-2">
              Vonage (skeleton) {providers.vonage?.enabled && providers.vonage?.api_secret_set ? <Badge>aktywny</Badge> : <Badge variant="outline" className="opacity-50">nieaktywny</Badge>}
            </h3>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!providers.vonage?.enabled} onChange={(e) => updateProvider('vonage', { enabled: e.target.checked })} /> enabled
            </label>
          </div>
          <p className="text-xs opacity-60">Vonage wymaga JWT signing — pełna integracja gdy podasz credentials.</p>
          <div className="grid grid-cols-2 gap-2">
            <Input value={providers.vonage?.api_key || ''} onChange={(e) => updateProvider('vonage', { api_key: e.target.value })} placeholder="api_key" />
            <Input type="password" placeholder="api_secret" onKeyDown={(e) => { if (e.key === 'Enter') { updateProvider('vonage', { api_secret: e.target.value }); e.target.value = ''; } }} />
          </div>
          <Input value={providers.vonage?.from_number || ''} onChange={(e) => updateProvider('vonage', { from_number: e.target.value })} placeholder="from_number +48..." />
        </CardContent>
      </Card>

      {/* TEST */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <h3 className="font-semibold">Test wywołania</h3>
          <div className="grid grid-cols-2 gap-2">
            <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="+48... numer do zadzwonienia" />
            <Button onClick={testCall} disabled={calling}>{calling ? 'Dzwonię…' : 'Zadzwoń'}</Button>
          </div>
          <textarea value={testMsg} onChange={(e) => setTestMsg(e.target.value)} className="w-full p-2 rounded border border-border bg-background text-sm min-h-16" placeholder="Wiadomość TTS (Twilio Say pl-PL)" />
        </CardContent>
      </Card>
    </div>
  );
}
